import React, { useEffect, useMemo } from 'react';
import {
  Collapse,
  Card,
  Form,
  Row,
  Tabs,
  Tag,
  Col,
  Typography,
  Image,
  Spin,
} from 'antd';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import Value from 'components/Value';
import FormInput from 'components/FormInput';
import ScheduleButton from 'components/ScheduleButton';
import FinancialScheduleModal from 'components/FinancialScheduleModal';
import { PlusOutlined } from '@ant-design/icons';
import { groupBy, isEmpty, isNil } from 'lodash';
import UtinaCard from 'components/UtinaCard';
import UtinaPanel from 'components/UtinaPanel/UtinaPanel';
import { toCurrency, toPercentage } from 'utils/utils';
import { AssetResponse } from 'services/proposal';
import IrvScheduleModal from 'components/IrvScheduleModal';
import SplitAssetButton from 'pages/ProposalV3/PropDetail/components/SplitAssetButton';
import IrvScheduleButton from 'components/IrvScheduleButton';
import DF from 'DisplayFramework';
import { useQuery } from 'react-query';
import { getFinContractById } from 'services/fincontract';
import { useParams } from 'react-router-dom';
import FormItem from 'antd/es/form/FormItem';
import { conElementToElement } from 'pages/ProposalV3/PropDetail/conElementToElement';
import { toDatePickerDate } from 'utils/utils2';
import GetLabel from 'DisplayFramework/components/GetLabel';
import CIList from 'pages/ClientInvoice/CIList';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import ParListSelect from 'components/ParListSelect';
import { UPLOAD_IMG_URL } from 'utils/config';
import ModalControllerNV from 'components/ModalControllerNV';
import AssetDetails from 'pages/Asset/AssetDetail';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import Button from 'components/Button';
import IActions from 'icons/IActions';
import ThirdPartyInput from 'components/ThirdPartyInput';
import SimpleServElementModal from 'pages/ProposalV3/PropDetail/SimpleServElementModal';

export type QuickSimulationProps = {
  reftechProp?: () => void;
  ctstatus?: string;
};
const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const SimpleQuickSimulation = ({
  reftechProp,
  ctstatus,
}: QuickSimulationProps) => {
  const { id } = useParams<{
    id: string;
  }>();

  const [form] = Form.useForm();
  const fieldName = 'portail';
  const {
    data: finContract,
    refetch,
    isFetching,
  } = useQuery(
    ['getFinContractById', id],
    () => getFinContractById({ ctrid: Number(id) }).then((res) => res.data),
    { enabled: !isNil(id) }
  );

  useEffect(() => {
    if (finContract) {
      let tp = finContract?.thirdParties.find(
        (elem: any) => elem.tprolecode === 'CLIENT'
      );
      form.resetFields();
      const {
        FIN = [],
        SERV = [],
        FEES = [],
        COMM = [],
        FINSERV = [],
      } = groupBy(
        conElementToElement(finContract.conElements, finContract.offer) || [],
        'ctetype'
      );

      let conElements: any[] = finContract?.conElements;

      const elemFin = finContract?.conElements?.find(
        (e) => e.ctetype === 'FIN' && e.ctestatus !== 'TERM'
      );
      const durationDefault = finContract?.conElements?.find(
        (e) => e.ctetype === 'FIN'
      )?.cteduration;

      const notInvoicedSchedule = finContract?.conElements
        ?.find((e) => e.ctetype === 'FIN')
        ?.conElemSchedules.filter(
          (s) =>
            s.ciid === null &&
            s.ctstype !== 'UPFRONT' &&
            s.ctstype !== 'RV' &&
            s.ctstype !== 'DEFINT'
        );

      const invoicedSchedule = finContract.conElements
        ?.find((e) => e.ctetype === 'FIN')
        ?.conElemSchedules.filter(
          (s) =>
            (s.ctsstatus === 'INVOICED' && s.ctstype === 'RENTAL') ||
            s.ctstype === 'FIRSTRENT' ||
            s.ctstype === 'GRACE'
        );

      const maxDueDate = invoicedSchedule
        ? invoicedSchedule.reduce(
            (maxDate, current) =>
              current.ctsduedate > maxDate ? current.ctsduedate : maxDate,
            ''
          )
        : '';

      const remainDuration = notInvoicedSchedule?.filter(
        (e) => e.ctsduedate >= maxDueDate
      ).length;

      const outstanding = invoicedSchedule?.find(
        (e) => e.ctsduedate >= maxDueDate
      )?.ctsoutstandingamount;

      form.setFieldsValue({
        durationDefault: durationDefault,
        cteduration: remainDuration ?? durationDefault,
        outstandingamount: outstanding ?? finContract.ctfinancedamount,
        ThirdParties: finContract?.thirdParties,
        flagmasterfacility: finContract.flagmasterfacility,
        flagasset: finContract?.flagasset,
        flagrealestate: finContract.flagrealestate,
        mfprcode: finContract.mfprcode,
        selectedElement: conElements?.find((e: any) => e.ctetype === 'FIN')
          ?.cteid,
        assets: finContract.assets,
        brid: finContract.brid,
        channelcode: '',
        ctnetworkcode: finContract.ctnetworkcode,
        client: {
          tpname: finContract.clientname,
          tpreference: finContract.clientreference,
          tpid: finContract.thirdParties[0]?.tpid,
        },
        clientname: finContract.clientname,
        clientreference: finContract.clientreference,
        ctcomment: finContract?.ctcomment,
        ctvaliditydate: finContract?.ctvaliditydate,
        ctsignaturedate: finContract?.ctsignaturedate,
        ctacceptancedate:
          finContract?.ctacceptancedate ??
          finContract?.ctdecisioncodelabel +
            ' - ' +
            (finContract?.ctdecisiondate ?? ''),
        ctactivationdate: finContract.ctactivationdate,
        ctbankname: finContract.ctbankname,
        ctbic: finContract.ctbic,
        ctbillingdate: finContract.ctbillingdate,
        ctcbdate: finContract.ctcbdate,
        ctcbkey: finContract.ctcbkey,
        ctcbname: finContract.ctcbname,
        ctcbnum: finContract.ctcbnum,
        ctcbtype: finContract.ctcbtype,
        ctdeliverydate: finContract.ctdeliverydate,
        ctdescription: finContract.ctdescription,
        ctenddate: finContract.ctenddate,
        ctfinancedamount: finContract.ctfinancedamount,
        ctflagreminder: finContract.ctflagreminder,
        ctiban: finContract.ctiban,
        ctidautorization: finContract.ctidautorization,
        assetpictureurl: finContract.assetpictureurl,
        ctinvoiceaddresscity: finContract.ctinvoiceaddresscity,
        ctinvoiceaddressregion: finContract.tpinvoiceaddressregion,
        ctinvoiceaddressname: finContract.ctinvoiceaddressname,
        ctinvoiceaddressstr1: finContract.ctinvoiceaddressstr1,
        ctinvoiceaddressstr2: finContract.ctinvoiceaddressstr2,
        ctinvoiceaddresszipcode: finContract.ctinvoiceaddresszipcode,
        tpinvoiceaddresscountry:
          finContract?.thirdParties[0]?.address?.tpmainadrcountrylabel,
        ctinvoicesendingmode: finContract.ctinvoicesendingmode,
        ctmodificationfeesformula: finContract.ctmodificationfeesformula,
        ctinvoicingmode: finContract.ctinvoicingmode,
        ctpartialpayoffformula: finContract.ctpartialpayoffformula,
        ctlateinterestformula: finContract.ctlateinterestformula,
        cttotalpayoffformula: finContract.cttotalpayoffformula,
        ctdegradationfeesformula: finContract.ctdegradationfeesformula,
        ctorderdate: finContract.ctorderdate,
        ctpaymentdelay:
          finContract.ctpaymentdelay !== null
            ? finContract.ctpaymentdelay
            : tp?.tppaydelayin,
        ctpaymentmode:
          finContract.ctpaymentmode !== null
            ? finContract.ctpaymentmode
            : tp?.tppaymodein,

        propreference: finContract.propreference,
        ctreference: finContract.ctreference,
        currcode: finContract.currcode,
        ctstatuslabel:
          finContract.ctstatuslabel +
          ' (' +
          finContract.ctstatusdate +
          ')' +
          (finContract.ctreversalreasonlabel !== null &&
          finContract.ctreversalreasonlabel !== undefined &&
          finContract.ctreversalreasonlabel !== ''
            ? ' - ' + finContract.ctreversalreasonlabel
            : ''),
        ctrequestdate: finContract.ctrequestdate,
        ctinitstartdate: finContract.ctinitstartdate,
        ctinitenddate: finContract?.ctinitenddate,
        ctdurationmonths: finContract.ctdurationmonths ?? 0,
        cttype: finContract.cttype,
        ctfclevel: finContract.ctfclevel,
        description: finContract.ctdescription,

        elements: {
          COMM,
          FEES,
          FIN,
          FINSERV,
          SERV,
        },

        prcode: finContract.prcode,
        flagspv: finContract.prcode === 'SPV',
        offerid: finContract.offerid,
        offerlabel: finContract.offer.offerlabel,
        pricing: finContract.conElements?.find((e: any) => e.ctetype === 'FIN')
          ?.ofrlabel,
        pritype: finContract.pritype,

        tpnameCOB: finContract.cobname,
        tpreferenceCOB: finContract.cobreference,
        useridsale: '',
        vendorid: '',
        syndication:
          finContract?.syndication?.map((synd) => ({
            ...synd,
            partamount:
              finContract.ctfinancedamount * (Number(synd.partcoef) / 100),
            tpid: {
              tpid: synd.tpid,
              tpname: synd.partnername,
              tpreference: synd.partnerreference,
            },
          })) ?? [],
        syndlead: {
          tpid: finContract.tpidsyndlead,
          tpname: finContract.syndleadname,
          tpreference: finContract.syndleadreference,
        },
        cteinvestamount: finContract.conElements?.find(
          (e: any) => e.ctetype === 'FIN'
        )?.cteinvestamount,
        ctervamount: finContract.conElements?.find(
          (e: any) => e.ctetype === 'FIN'
        )?.ctervamount,
        ctefinancedamount: finContract.ctfinancedamount,
        ctsyndinvoicingtype: finContract.ctsyndinvoicingtype,
        ctsyndsuppliermanagementtype: finContract.ctsyndsuppliermanagementtype,
        ctsyndpaymentmanagementtype: finContract.ctsyndpaymentmanagementtype,
        ctsyndmgcoef: finContract.ctsyndmgcoef,
        ctsyndmgpartamount: finContract.ctsyndmgpartamount,
        thirdparties:
          finContract?.thirdParties?.map((e) => {
            if (e.tprolecode === 'MFBENEF') {
              return {
                ...e,
                mfrequestedamount:
                  e.mfrequestedamount === 0 || isNil(e.mfrequestedamount)
                    ? finContract.mfrequestedamount
                    : e.mfrequestedamount,
                tpid: {
                  tpid: e.tpid,
                  tpname: e.tpname,
                  tpreference: e.tpreference,
                },
                startdate: toDatePickerDate(e.startdate),
                enddate: toDatePickerDate(e.enddate),
              };
            } else
              return {
                ...e,
                tpid: {
                  tpid: e.tpid,
                  tpname: e.tpname,
                  tpreference: e.tpreference,
                },
                startdate: toDatePickerDate(e.startdate),
                enddate: toDatePickerDate(e.enddate),
              };
          }) ?? [],
        mfagreedamount: finContract.mfagreedamount,
        mfavailableamount: finContract.mfavailableamount,
        mfbookedamount: finContract.mfbookedamount,
        mfflagrevolving: finContract.mfflagrevolving,
        mfrequestedamount: finContract.mfrequestedamount,
        mfusedamount: finContract.mfusedamount,
        mfvaliditydate: finContract.mfvaliditydate,
        offerParams: finContract.offer,
        refetch: false,

        ctendofterm: finContract?.ctendofterm || null,
        ctendoftermtaskid: finContract?.ctendoftermtaskid || null,
        ctendoftermdelay: finContract?.ctendoftermdelay || null,
        totalunpaidamount: finContract.totalunpaidamount,
      });
    }
  }, [id, finContract]);

  const columnsasset = useMemo<DataGridColDef<AssetResponse>[]>(
    () => [
      {
        comcode: 'Asset.assetreference',
        dataIndex: 'assetreference',
        render: (value, record) => record.assetreference,
        width: 140,
      },

      {
        comcode: 'Asset.assetdescription',
        dataIndex: 'assetlabel',
        width: 250,
        render: (assetlabel, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{assetlabel}</a>
            )}
            modal={({ setVisible, visible }) => (
              <AssetDetails
                assetid={record.assetid}
                asset={record}
                onCancel={() => setVisible(false)}
                showAsModal
                display
                isproposalcreate={isNil(id)}
                visible={visible}
                hideSaveBtn
                phaseprop="PROD"
                hideActionBtn
                proposalData={finContract}
                ctstatus={ctstatus}
              />
            )}
          />
        ),
      },
      {
        comcode: 'Asset.assettypelabel',
        dataIndex: 'assettypelabel',
        width: 200,
      },
      {
        comcode: 'Asset.assetcategorylabel',
        dataIndex: 'assetcategorylabel',
        width: 200,
      },
      {
        comcode: 'Asset.suppliername',
        dataIndex: 'suppliername',
        width: 250,
      },

      {
        comcode: 'Asset.assetstatuslabel',
        dataIndex: 'statuslabel',
        width: 100,
      },
      {
        comcode: 'Asset.assetdeliverydate',
        dataIndex: 'assetdeliverydate',
        width: 100,
      },
      {
        align: 'right',
        comcode: 'Asset.assetunitnetamount',
        dataIndex: 'assetunitnetamount',
        render: (val) =>
          toCurrency(val, localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetqty',
        dataIndex: 'assetqty',
        width: 100,
      },
      {
        align: 'right',
        comcode: 'Asset.assetnetamount',
        dataIndex: 'assetnetamount',
        render: (val) =>
          toCurrency(val, localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetrvamount',
        dataIndex: 'assetrvamount',
        render: (val) =>
          toCurrency(val, localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetfinamount',
        dataIndex: 'assetfinamount',
        render: (val) =>
          toCurrency(val, localStorage.getItem('currcode') ?? ''),
        width: 150,
      },

      {
        align: 'right',
        comcode: 'Asset.assettaggrouping',
        dataIndex: 'assettaggrouping',
        render: (val) => (
          <Tag color="blue" key={val}>
            {val}
          </Tag>
        ),
        width: 150,
      },
      {
        key: 'actions',
        comcode: 'actions',
        render: (_, record) => (
          <>
            {Number(id)! > 0 &&
            record?.assetqty &&
            record?.assetqty > 1 &&
            !['ORDER', 'DELIVER'].includes(record.assetstatus) ? (
              <SplitAssetButton
                reftechProp={reftechProp}
                assetid={record.assetid}
                comcode="split"
                ctrid={Number(id)!}
                qty={record.assetqty}
              />
            ) : null}

            {record.irvSchedule?.length > 0 && (
              <IrvScheduleModal
                irvSchedule={record.irvSchedule}
                currcode={record.currcode}
                controller={
                  <IrvScheduleButton
                    comcode="irvScheduleButton"
                    twoToneColor="red"
                    style={{
                      height: 40,
                      marginLeft: 5,
                      marginRight: 5,
                      marginBottom: 5,
                    }}
                    onlyicon
                  />
                }
              />
            )}
          </>
        ),
        fixed: 'right',
        width: 80,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        comcode: 'Element.ctetypelabel',
        dataIndex: 'ctetypelabel',
        width: '20%',
        render: (value, record) =>
          record.serviceResponse?.servtypelabel ??
          record.commissionResponse?.commtypelabel ??
          record.ctetype,
      },
      {
        comcode: 'Element.servcategorylabel',
        dataIndex: 'ctecategorylabel',
        width: '20%',
        render: (value, record) =>
          record.ctetype === 'FIN'
            ? record.ctecategory
            : record.serviceResponse?.servcategorylabel ?? record.ctecategory,
      },

      {
        comcode: 'Element.ctedescription',
        dataIndex: 'ctedescription',
        width: '30%',
        render: (ctedescription, record) =>
          record.ctetype === 'SERV' && (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{ctedescription}</a>
              )}
              modal={({ setVisible, visible }) => (
                <SimpleServElementModal
                  offerid={form.getFieldValue('offerid')}
                  visible={visible}
                  onClose={() => setVisible(false)}
                  record={record}
                  onSave={() => {}}
                  updateService
                  assetamount={record.assetamount ?? 0}
                  cteduration={record.cteduration ?? 0}
                  ctefinancedamount={record.ctefinancedamount ?? 0}
                  ctegraceperiodduration={record?.ctegraceperiodduration ?? 0}
                  cteinvestamount={record.cteinvestamount ?? 0}
                  ctestartdate={record.ctestartdate ?? ''}
                  finschedule={record.finschedule ?? []}
                  assets={form?.getFieldValue('assets')}
                  cteidparent={form?.getFieldValue('selectedElement')}
                  hidebtn
                />
              )}
            ></ModalControllerNV>
          ),
      },
      {
        comcode: 'Element.tpname',
        dataIndex: 'tpname',
        width: '20%',
        render: (value, record) =>
          record.serviceResponse?.tpname ??
          record.commissionResponse?.commlabel,
      },
      {
        comcode: 'Element.servbasislabel',
        dataIndex: 'servbasislabel',
        width: '20%',
        render: (value, record) =>
          record.serviceResponse?.servbasislabel ??
          record.commissionResponse?.commbasislabel,
      },

      {
        comcode: 'Element.servcalculationmethodlabel',
        dataIndex: 'servcalculationmethodlabel',
        width: '35%',
        render: (value, record) =>
          record.serviceResponse?.servcalculationmethodlabel ??
          record.commissionResponse?.commcalculationmethodlabel,
      },
      {
        comcode: 'perOrFixedAmount',
        dataIndex: 'perOrFixedAmount',
        render: (value, record) =>
          record.serviceResponse
            ? record.serviceResponse?.servcalculationmethod === 'PERCENT'
              ? toPercentage(record.serviceResponse?.servpercentage)
              : toCurrency(
                  record.serviceResponse?.servfixedamount,
                  record.currcode
                )
            : record.commissionResponse?.commcalculationmethod === 'PERCENT'
            ? toPercentage(record.commissionResponse?.commpercentage)
            : toCurrency(
                record.commissionResponse?.commfixedamount,
                record.currcode
              ),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'Element.servdurationlabel',
        dataIndex: 'servdurationlabel',
        width: '40%',
        render: (value, record) =>
          record.serviceResponse?.servdurationlabel ??
          record.commissionResponse?.commdurationlabel,
      },
      {
        comcode: 'Element.cteinstallmentamount',
        dataIndex: 'cteinstallmentamount',
        render: (value, record) => toCurrency(value, record.currcode),
        fixed: 'right',
        width: '20%',
        align: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const actions = (
    <div>
      <Form.Item shouldUpdate={() => true}>
        {({ getFieldValue }) => {
          return (
            <TasksListPopover
              entityId={finContract?.ctrid ?? 0}
              tpid={finContract?.tpidclient}
              entityReference={finContract?.ctreference}
              entityName="CONTRACT"
              requestedamount={finContract?.ctfinancedamount}
              currcode={finContract?.currcode}
              title={
                finContract?.propreference + ' - ' + finContract?.clientname
              }
              clientName={finContract?.clientname}
              onClose={refetch}
              prcode={finContract?.prcode}
            >
              <Button
                comcode="actions"
                style={{ width: 120, height: 40 }}
                icon={<IActions />}
              />
            </TasksListPopover>
          );
        }}
      </Form.Item>
    </div>
  );
  return (
    <DF.Provider
      pagcode="contractforportail"
      entity="PROPOSAL"
      entityid={Number(id)}
    >
      <Spin spinning={isFetching}>
        <Form form={form}>
          <Row style={{ height: 20 }}>
            <Col span={22}>
              <strong>
                {<GetLabel label="Contract" /> + ' - ' + finContract &&
                  finContract?.ctreference + ' - ' + finContract?.clientname}
              </strong>
            </Col>
            <Col span={2}>
              <Form.Item shouldUpdate={() => true}>
                {({ getFieldValue }) => {
                  return (
                    <TasksListPopover
                      entityId={finContract?.ctrid ?? 0}
                      tpid={finContract?.tpidclient}
                      entityReference={finContract?.ctreference}
                      entityName="CONTRACT"
                      requestedamount={finContract?.ctfinancedamount}
                      currcode={finContract?.currcode}
                      title={
                        finContract?.propreference +
                        ' - ' +
                        finContract?.clientname
                      }
                      clientName={finContract?.clientname}
                      onClose={refetch}
                      prcode={finContract?.prcode}
                    >
                      <Button
                        comcode="actions"
                        style={{ width: 120, height: 40 }}
                        icon={<IActions />}
                      />
                    </TasksListPopover>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Tabs tabPosition="left">
            <TabPane
              tab={<GetLabel label="Quick Simul" />}
              key="1"
              style={{ padding: 30, height: '100%' }}
              active
            >
              <Collapse
                bordered={false}
                defaultActiveKey={['1']}
                expandIcon={({ isActive }) => (
                  <PlusOutlined style={{ fontSize: 17 }} />
                )}
                expandIconPosition="left"
              >
                <UtinaPanel
                  comcode="general data"
                  key="1"
                  extra={
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => {
                        return (
                          <FinancialScheduleModal
                            finElement={getFieldValue(['elements', 'FIN'])}
                            global
                            currcode={getFieldValue('currcode')}
                            data={getFieldValue('elements')}
                            comcode="globalSchedule"
                            controller={
                              <ScheduleButton
                                size="large"
                                comcode="globalSchedule"
                                twoToneColor={
                                  isNil(getFieldValue('offerid'))
                                    ? 'gray'
                                    : 'green'
                                }
                              ></ScheduleButton>
                            }
                          />
                        );
                      }}
                    </Form.Item>
                  }
                >
                  <Card size="small" style={{ paddingLeft: 10 }}>
                    <Row gutter={16}>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          return (
                            <UtinaCard
                              level={5}
                              lg={11}
                              comcode="General Data"
                              additionalAttribute
                            >
                              <Row justify="center" style={{ paddingLeft: 40 }}>
                                <Col span={12}>
                                  <FormInput xs={24} md={4} name="ctreference">
                                    <Value></Value>
                                  </FormInput>
                                </Col>
                                <Col span={12}>
                                  <FormInput xs={20} name="ctstatuslabel">
                                    <Value />
                                  </FormInput>
                                </Col>

                                <Col span={12}>
                                  <Form.Item shouldUpdate noStyle>
                                    <FormInput
                                      col
                                      xs={24}
                                      md={16}
                                      name="prcode"
                                    >
                                      <ProductInput disabled />
                                    </FormInput>
                                  </Form.Item>
                                </Col>

                                <Col span={12}>
                                  <FormInput col xs={20} name="ctinitstartdate">
                                    <Value />
                                  </FormInput>
                                </Col>

                                <Col span={12}>
                                  <FormInput xs={20} name="offerlabel">
                                    <Value />
                                  </FormInput>
                                </Col>

                                <Col span={12}>
                                  <Form.Item shouldUpdate noStyle>
                                    <FormInput
                                      col
                                      xs={24}
                                      md={8}
                                      name="pricing"
                                    >
                                      <Value></Value>
                                    </FormInput>
                                  </Form.Item>
                                </Col>

                                <Col span={12}>
                                  <Form.Item shouldUpdate noStyle>
                                    <FormInput
                                      col
                                      xs={24}
                                      md={16}
                                      name="ctnetworkcode"
                                    >
                                      <ParListSelect
                                        parcode="NETWORK"
                                        disabled
                                      />
                                    </FormInput>
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <FormInput
                                    col
                                    xs={20}
                                    name="ctactivationdate"
                                  >
                                    <Value />
                                  </FormInput>
                                </Col>
                                <Col span={12}>
                                  <Form.Item shouldUpdate noStyle>
                                    <FormInput col name="client">
                                      <ThirdPartyInput display />
                                    </FormInput>
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  {/* <FormInput 
                            col
                            xs={20} 
                            name="tptel1"
                            noMargin
                            icon={
                                <PhoneFilled
                                  style={{ color: '#008F41', fontSize: 15 }}
                                />
                            }
                          >
                            <Value />
                          </FormInput> */}
                                </Col>
                              </Row>
                            </UtinaCard>
                          );
                        }}
                      </Form.Item>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          return (
                            <UtinaCard level={5} lg={6} comcode="Synthesis">
                              <FormInput col xs={20} name="cteinvestamount">
                                <Text>
                                  {toCurrency(
                                    getFieldValue('cteinvestamount'),
                                    finContract?.currcode
                                  )}
                                </Text>
                              </FormInput>

                              <FormInput col xs={24} name="ctfinancedamount">
                                <Text>
                                  {toCurrency(
                                    getFieldValue('ctfinancedamount'),
                                    finContract?.currcode
                                  )}
                                </Text>
                              </FormInput>
                              <FormInput col xs={24} name="outstandingamount">
                                <Text>
                                  {toCurrency(
                                    getFieldValue('outstandingamount'),
                                    finContract?.currcode
                                  )}
                                </Text>
                              </FormInput>
                              <FormInput col xs={24} name="ctervamount">
                                <Text>
                                  {toCurrency(
                                    getFieldValue('ctervamount'),
                                    finContract?.currcode
                                  )}
                                </Text>
                              </FormInput>
                              <FormInput col xs={24} name="totalunpaidamount">
                                <Text>
                                  {toCurrency(
                                    getFieldValue('totalunpaidamount'),
                                    finContract?.currcode
                                  )}
                                </Text>
                              </FormInput>
                              <FormInput col xs={24} name="duration">
                                <Text>
                                  {form.getFieldValue('cteduration') +
                                    ' / ' +
                                    getFieldValue('durationDefault')}
                                </Text>
                              </FormInput>
                            </UtinaCard>
                          );
                        }}
                      </Form.Item>

                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          return (
                            getFieldValue('flagasset') && (
                              <UtinaCard
                                level={5}
                                lg={5}
                                comcode="assetPicture"
                                additionalAttribute
                              >
                                <Image
                                  preview={{ visible: false }}
                                  src={
                                    UPLOAD_IMG_URL +
                                    form.getFieldValue('assetpictureurl')
                                  }
                                />
                              </UtinaCard>
                            )
                          );
                        }}
                      </Form.Item>
                    </Row>
                  </Card>
                </UtinaPanel>
              </Collapse>

              <FormItem shouldUpdate noStyle>
                <Collapse
                  bordered={false}
                  defaultActiveKey={['1']}
                  expandIcon={({ isActive }) => (
                    <PlusOutlined style={{ fontSize: 17 }} />
                  )}
                  expandIconPosition="left"
                  style={{ background: 'none !important' }}
                >
                  <UtinaPanel comcode="assetList" key={2}>
                    <Card>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const cteid = getFieldValue('selectedElement') ?? 0;
                          const assets = finContract?.assets
                            ?.filter((a: any) => a.cteid === cteid)
                            ?.map((asset: any) =>
                              !isEmpty(asset?.irvSchedule)
                                ? {
                                    ...asset,
                                    rentalpart: asset?.irvSchedule?.filter(
                                      (e: any) => e.ctstype === 'RENTAL'
                                    )[0]?.rentalpart,
                                  }
                                : asset
                            );

                          return (
                            <DataGrid
                              size="middle"
                              columns={columnsasset}
                              dataSource={assets}
                              rowKey="assetid"
                              pagination={false}
                            />
                          );
                        }}
                      </Form.Item>
                    </Card>
                  </UtinaPanel>
                </Collapse>
              </FormItem>
              <FormItem shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  return (
                    <Collapse
                      bordered={false}
                      expandIcon={({ isActive }) => (
                        <PlusOutlined style={{ fontSize: 17 }} />
                      )}
                      expandIconPosition="left"
                      style={{ background: 'none !important' }}
                    >
                      <UtinaPanel comcode="servicesToFinance" key={8}>
                        <Form.Item shouldUpdate>
                          {({ getFieldValue }) => {
                            const SERV: any[] = Array.isArray(
                              getFieldValue(['elements', 'SERV'])
                            )
                              ? getFieldValue(['elements', 'SERV'])
                              : [];
                            const FEES: any[] = Array.isArray(
                              getFieldValue(['elements', 'FEES'])
                            )
                              ? getFieldValue(['elements', 'FEES'])
                              : [];

                            const elements = [...SERV, ...FEES];
                            return (
                              <DataGrid
                                rowKey={(element) => element.cteid}
                                dataSource={elements}
                                columns={columns}
                              />
                            );
                          }}
                        </Form.Item>
                      </UtinaPanel>
                    </Collapse>
                  );
                }}
              </FormItem>
            </TabPane>

            <TabPane tab={<GetLabel label="Client Invoice" />} key="2">
              <CIList
                ctreference={finContract?.ctreference}
                hideExtraAction
                hideFilters
                hideContractCol
              />
            </TabPane>
          </Tabs>
        </Form>
      </Spin>
    </DF.Provider>
  );
};

export default SimpleQuickSimulation;
